/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-nested-ternary */
import React, { CSSProperties, FC, ReactNode, useState } from 'react'
import styles from './BestListComparisonTableContent.module.css'
import {
  BasicStyledComponent,
  DSBestListRatingsTableCell,
  DSBestListRatingsTableCellVote,
  DSBestListRatingsTableColumn,
  DSBestListRatingsTableContent,
  DSBestListRatingsTableRow,
  DSSortValue,
  DSSubmitProductVoteResult,
  DSVote,
} from 'shared-definitions/types'
import clsx from 'clsx'
import { CheckDetailsProductCell } from './CheckDetailsProductCell'
import ProductVotes from './ProductVotes'
import { pxToRem } from 'shared-code/conversion'
import { useElementResize } from '../hooks/use-element-resize'

export interface BestListComparisonTableContentProps extends BasicStyledComponent {
  content: DSBestListRatingsTableContent
  vote: (productId: string, vote: DSVote) => Promise<DSSubmitProductVoteResult | null>
  onSeeMore: (productId: string, signal?: AbortSignal) => void
  sortValue: DSSortValue | null
  onSortValueChange: (sortValue: DSSortValue | null) => void
}

export const BestListComparisonTableContent: FC<BestListComparisonTableContentProps> = ({
  className,
  content,
  sortValue,
  onSortValueChange,
  vote,
  onSeeMore,
}) => {
  const [shifts, setShifts] = useState<Record<string, number>>({})

  const ref = useElementResize<HTMLDivElement>(el => {
    const firstRow = el.querySelector('[data-head]')

    if (!firstRow) {
      return
    }

    const newShifts: Record<string, number> = {}

    let prevShift = 0
    content.columns.forEach((column, index) => {
      if (!column.pinned) {
        return
      }

      const columnEl = firstRow.querySelector(`[data-key="${index.toString()}"]`)
      if (!columnEl) {
        return
      }

      // add the width of every previously pinned column
      newShifts[index.toString()] = prevShift
      prevShift += columnEl.getBoundingClientRect().width
    })

    console.log('shifts')
    setShifts(newShifts)
  })

  if (content.rows.length <= 0) {
    return null
  }

  function getShiftVar(columnKey: string): undefined | CSSProperties {
    const val = shifts[columnKey]
    return val !== undefined
      ? ({
          '--column-shift': `${val}px`,
        } as CSSProperties)
      : undefined
  }

  function isMobileColumnHidden(column: DSBestListRatingsTableColumn): boolean {
    return column.columnId === 'votes' || column.columnId === 'price'
  }

  function isMobileCellHidden(_cell: DSBestListRatingsTableCell, cellIndex: number): boolean {
    const column = content.columns[cellIndex]
    if (column && isMobileColumnHidden(column)) {
      return true
    }

    return false
  }

  function getVotesCell(row: DSBestListRatingsTableRow): DSBestListRatingsTableCellVote | null {
    for (const cell of row.cells) {
      if (cell.type === 'vote') {
        return cell
      }
    }

    return null
  }

  return (
    <div
      ref={ref}
      className={clsx(styles.container, className)}
      style={
        {
          '--grid-rules-desktop': getGridRulesDesktop(content.rows[0].cells),
          '--grid-rules-mobile': getGridRulesMobile(content.rows[0].cells, isMobileCellHidden),
        } as CSSProperties
      }
    >
      <ul className={styles.table}>
        <li className={styles.tableRow} data-head>
          {content.columns.map((column, index) => {
            if (!column.sortable) {
              return (
                <span
                  style={getShiftVar(index.toString())}
                  data-key={index.toString()}
                  key={index}
                  className={clsx(styles.colTitle, styles.tableHeaderCell, {
                    [styles.pinned]: column.pinned,
                    [styles.mobileColumnHidden]: isMobileColumnHidden(column),
                  })}
                >
                  <span className={styles.colTitleText}>{column.title}</span>
                </span>
              )
            }

            const onSortClick = (): void => {
              if (column.columnId === sortValue?.field) {
                onSortValueChange({
                  field: column.columnId,
                  order: sortValue.order === 'asc' ? 'desc' : 'asc',
                })
              } else {
                onSortValueChange({
                  field: column.columnId,
                  order: 'asc',
                })
              }
            }

            return (
              <button
                type="button"
                onClick={onSortClick}
                style={getShiftVar(index.toString())}
                data-key={index.toString()}
                key={index}
                className={clsx(styles.colTitle, styles.tableHeaderCell, styles.colTitleSortable, {
                  [styles.mobileColumnHidden]: isMobileColumnHidden(column),
                  [styles.colTitleSortedAsc]:
                    sortValue?.field === column.columnId && sortValue.order === 'asc',
                  [styles.colTitleSortedDesc]:
                    sortValue?.field === column.columnId && sortValue.order === 'desc',
                  [styles.pinned]: column.pinned,
                })}
              >
                <span className={styles.colTitleText}>{column.title}</span>
              </button>
            )
          })}
        </li>
        {content.rows.map(row => (
          <li className={styles.tableRow} key={row.id}>
            {row.cells.map((cell, cellIndex) => {
              const column = content.columns[cellIndex] ?? { pinnded: false }
              let cellContent: ReactNode = cell.type
              switch (cell.type) {
                case 'numeric':
                  cellContent = (
                    <p
                      style={getShiftVar(cellIndex.toString())}
                      key={cellIndex}
                      className={clsx(styles.cellNumeric, styles.tableCell, {
                        [styles.pinned]: column.pinned,
                        [styles.mobileCellHidden]: isMobileCellHidden(cell, cellIndex),
                      })}
                    >
                      {cell.label}
                    </p>
                  )
                  break
                case 'text':
                  cellContent = (
                    <p
                      style={getShiftVar(cellIndex.toString())}
                      key={cellIndex}
                      className={clsx(styles.cellText, styles.tableCell, {
                        [styles.pinned]: column.pinned,
                        [styles.mobileCellHidden]: isMobileCellHidden(cell, cellIndex),
                      })}
                    >
                      {cell.text}
                    </p>
                  )
                  break
                case 'product':
                  cellContent = (
                    <div
                      style={getShiftVar(cellIndex.toString())}
                      key={cellIndex}
                      className={clsx(styles.cellProduct, styles.tableCell, {
                        [styles.pinned]: column.pinned,
                        [styles.mobileCellHidden]: isMobileCellHidden(cell, cellIndex),
                      })}
                    >
                      <CheckDetailsProductCell
                        onSeeMore={onSeeMore}
                        key={cellIndex}
                        product={cell.product}
                        underImageSlot={((): ReactNode => {
                          const voteCell = getVotesCell(row)
                          if (!voteCell) {
                            return null
                          }
                          return (
                            <ProductVotes
                              style={getShiftVar(cellIndex.toString())}
                              key={cellIndex}
                              className={styles.cellVoteInProduct}
                              vote={vote}
                              data={voteCell}
                              variant="compact"
                            />
                          )
                        })()}
                      />
                    </div>
                  )
                  break
                case 'vote':
                  cellContent = (
                    <ProductVotes
                      style={getShiftVar(cellIndex.toString())}
                      key={cellIndex}
                      className={clsx(styles.cellVote, styles.tableCell, {
                        [styles.pinned]: column.pinned,
                        [styles.mobileCellHidden]: isMobileCellHidden(cell, cellIndex),
                      })}
                      vote={vote}
                      data={cell}
                      variant="compact"
                    />
                  )
                  break
              }
              return cellContent
            })}
          </li>
        ))}
      </ul>
    </div>
  )
}

function getGridRulesDesktop(cells: DSBestListRatingsTableCell[]): string {
  const rules: string[] = []

  cells.forEach(cell => {
    if (cell.type === 'product') {
      rules.push(`${pxToRem(364)}rem`)
    } else if (cell.type === 'vote') {
      rules.push(`${pxToRem(96)}rem`)
    } else {
      rules.push(`minmax(${pxToRem(100)}rem, 1fr)`)
    }
  })
  return rules.join(' ')
}

type IsMobileHiddenFn = (cell: DSBestListRatingsTableCell, index: number) => boolean

function getGridRulesMobile(
  cells: DSBestListRatingsTableCell[],
  isMobileHidden: IsMobileHiddenFn
): string {
  const rules: string[] = []

  cells.forEach((cell, index) => {
    if (isMobileHidden(cell, index)) {
      // hidden columns
    } else if (cell.type === 'product') {
      rules.push(`${pxToRem(234)}rem`)
    } else if (cell.type === 'vote') {
      rules.push(`${pxToRem(84)}rem`)
    } else {
      rules.push(`minmax(${pxToRem(70)}rem, 1fr)`)
    }
  })
  return rules.join(' ')
}
